import { Box, Stack, Typography } from '@mui/material';
import StringSelector from '../../string-selector';
import { VideoCarousel } from '../VideoCarousel/VideoCarouselModal';
import { CustomCarouselSelect } from './CustomCarouselSelect';
import { CategoryChannelSelect } from './CategoryChannelSelect';
import { ModuleTitleInput } from './ModuleTitleInput';
import type { FotbollPlayChannelOption } from '../../hooks/useFotbollPlayTeamChannels';
import type { ModuleOrderEntry } from '../../api/types';
import { isCategoryModule } from '../../constants';
import {
  createModuleEntry,
  getDefaultModuleTitle,
  getModuleKey,
  reorderModuleOrderByKeys,
  toChannelId,
} from '../../utils/module-order-utils';

interface ModuleOrderSectionProps {
  moduleOrder: ModuleOrderEntry[];
  carousels: VideoCarousel[];
  channels: FotbollPlayChannelOption[];
  availableCategories: string[];
  categoryLabels: Record<string, string>;
  onModuleOrderChange: (entries: ModuleOrderEntry[]) => void;
  onCustomCarouselSelectionChange: (moduleKey: string, carouselId: string) => void;
  onCategoryChannelSelectionChange: (moduleKey: string, channelIds: number[]) => void;
  onModuleTitleChange: (moduleKey: string, title: string) => void;
}

export function ModuleOrderSection({
  moduleOrder,
  carousels,
  channels,
  availableCategories,
  categoryLabels,
  onModuleOrderChange,
  onCustomCarouselSelectionChange,
  onCategoryChannelSelectionChange,
  onModuleTitleChange,
}: ModuleOrderSectionProps) {
  const allChannelIds = channels
    .map((channel) => toChannelId(channel.id))
    .filter((id): id is number => id !== null);
  const moduleKeys = moduleOrder.map(getModuleKey);
  const entryByKey = new Map(moduleOrder.map((entry) => [getModuleKey(entry), entry]));

  function handleModuleOrderChange(keys: string[]) {
    onModuleOrderChange(reorderModuleOrderByKeys(keys, moduleOrder, allChannelIds, categoryLabels));
  }

  function getOptionLabel(value: string) {
    if (value === 'custom-carousel') {
      return 'Custom Carousel';
    }

    const entry = entryByKey.get(value);
    if (entry?.type === 'category') {
      return categoryLabels[entry.category] ?? entry.category;
    }

    if (entry?.type === 'custom-carousel') {
      return 'Custom Carousel';
    }

    return categoryLabels[value] ?? value;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
        Moduler ordning
      </Typography>
      <StringSelector
        availableOptions={[...availableCategories, 'custom-carousel']}
        selectedValues={moduleKeys}
        onChange={handleModuleOrderChange}
        availableLabel="Tillgängliga"
        selectedLabel="Valda (dra för att ändra ordning)"
        getOptionLabel={getOptionLabel}
        getAddValue={(option) =>
          getModuleKey(createModuleEntry(option, allChannelIds, categoryLabels))
        }
        renderItemRightContent={(value) => {
          const entry = entryByKey.get(value);
          if (!entry) return null;

          const defaultTitle = getDefaultModuleTitle(entry, categoryLabels, carousels);

          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <ModuleTitleInput
                moduleKey={value}
                value={entry.title ?? ''}
                defaultTitle={defaultTitle}
                onChange={onModuleTitleChange}
              />
              {entry.type === 'custom-carousel' ? (
                <CustomCarouselSelect
                  moduleKey={value}
                  selectedId={entry.carouselId}
                  carousels={carousels}
                  onChange={onCustomCarouselSelectionChange}
                />
              ) : isCategoryModule(entry.category) ? (
                <CategoryChannelSelect
                  moduleKey={value}
                  selectedChannelIds={entry.channelIds}
                  channels={channels}
                  onChange={onCategoryChannelSelectionChange}
                />
              ) : null}
            </Stack>
          );
        }}
      />
    </Box>
  );
}
